import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";

const TYPES = ["individual", "corporation", "non_profit", "influencer", "production", "campaign"];
const STATUSES = ["lead", "active", "paused", "completed", "archived"];

export default function AdminClients() {
  const [items, setItems] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", organization: "", type: TYPES[0], status: STATUSES[0], notes: "" });

  const load = () => api.get("/admin/clients").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/clients", form);
      toast.success("Client added");
      setShowNew(false);
      setForm({ name: "", email: "", phone: "", organization: "", type: TYPES[0], status: STATUSES[0], notes: "" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this client?")) return;
    await api.delete(`/admin/clients/${id}`);
    load();
  };

  const updateStatus = async (id, status) => {
    await api.patch(`/admin/clients/${id}`, { status });
    load();
  };

  return (
    <div data-testid="admin-clients-page">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="eyebrow text-[var(--crest-gold)]">— Clients</div>
          <h1 className="font-serif text-4xl md:text-5xl tracking-tight mt-2">Client Management</h1>
        </div>
        <button className="btn-primary" onClick={() => setShowNew((v) => !v)} data-testid="clients-new-toggle"><Plus size={14} /> New Client</button>
      </div>

      {showNew && (
        <form onSubmit={create} className="admin-card grid md:grid-cols-2 gap-x-8 gap-y-2 mb-8" data-testid="client-create-form">
          <input className="crest-input md:col-span-2" placeholder="Name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="client-form-name" />
          <input type="email" className="crest-input" placeholder="Email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="client-form-email" />
          <input className="crest-input" placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="client-form-phone" />
          <input className="crest-input md:col-span-2" placeholder="Organization" value={form.organization} onChange={(e) => setForm({ ...form, organization: e.target.value })} data-testid="client-form-org" />
          <select className="crest-input" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="client-form-type">
            {TYPES.map((t) => <option key={t}>{t}</option>)}
          </select>
          <select className="crest-input" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="client-form-status">
            {STATUSES.map((s) => <option key={s}>{s}</option>)}
          </select>
          <textarea className="crest-input md:col-span-2" placeholder="Notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="client-form-notes" />
          <button className="btn-primary md:col-span-2 mt-4" data-testid="client-form-submit">Save Client</button>
        </form>
      )}

      <div className="admin-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[var(--crest-muted)] border-b border-black/10">
              <th className="py-3 px-2">Name</th>
              <th className="py-3 px-2">Email</th>
              <th className="py-3 px-2">Type</th>
              <th className="py-3 px-2">Status</th>
              <th className="py-3 px-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={5} className="py-10 text-center text-[var(--crest-muted)]">No clients yet.</td></tr>}
            {items.map((c) => (
              <tr key={c._id} className="border-b border-black/5" data-testid={`client-row-${c._id}`}>
                <td className="py-3 px-2 font-medium">{c.name}{c.organization ? ` · ${c.organization}` : ""}</td>
                <td className="py-3 px-2 text-[var(--crest-muted)]">{c.email}</td>
                <td className="py-3 px-2">{c.type}</td>
                <td className="py-3 px-2">
                  <select className="crest-input text-xs" value={c.status} onChange={(e) => updateStatus(c._id, e.target.value)} data-testid={`client-status-${c._id}`}>
                    {STATUSES.map((s) => <option key={s}>{s}</option>)}
                  </select>
                </td>
                <td className="py-3 px-2 text-right">
                  <button onClick={() => remove(c._id)} className="text-[var(--crest-muted)] hover:text-red-600" data-testid={`client-delete-${c._id}`}>
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
